import React, { useState, useEffect } from 'react';
import { 
  Card, Tabs, Form, Input, Button, notification, 
  Divider, Space, Row, Col, Avatar, Select, 
  Spin, Switch, Descriptions, Statistic, Upload
} from 'antd';
import { 
  UserOutlined, 
  MailOutlined, 
  PhoneOutlined, 
  HomeOutlined, 
  LockOutlined,
  SaveOutlined,
  EditOutlined,
  BuildOutlined,
  GlobalOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  UploadOutlined
} from '@ant-design/icons';
import userService from '../services/userService';
import authService from '../services/authService';
import usePasswordUpdate from '../hooks/usePasswordUpdate';
import config from '../config';

const { TabPane } = Tabs;
const { Option } = Select;

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [userStats, setUserStats] = useState({
    properties: 0,
    activeOrders: 0,
    completedOrders: 0,
    totalSpent: 0,
    lastOrder: null
  });
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [passwordForm] = Form.useForm();
  const [profileForm] = Form.useForm();
  const { loading: passwordLoading, updatePassword } = usePasswordUpdate();
  const [countries] = useState([
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'ES', name: 'Spain' },
    { code: 'IT', name: 'Italy' },
    { code: 'NL', name: 'Netherlands' }
  ]);

  const resolveAssetUrl = (value) => {
    if (!value) return '';
    const cleanValue = String(value).replace(/^(\.\.\/)+/, '').replace(/^\/+/, '');
    if (/^https?:\/\//i.test(cleanValue)) return cleanValue;
    return `${config.APP_URL}/${cleanValue}`;
  };

  // Fetch user data on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      
      // Get current user from localStorage
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser || !currentUser.id) {
        notification.error({
          message: 'Authentication Error',
          description: 'Could not retrieve user information. Please log in again.'
        });
        return;
      }
      
      // Fetch detailed user data
      const userData = await userService.getUserById(currentUser.id);
      setUser(userData);
      
      // Set form values with all required fields
      profileForm.setFieldsValue({
        // Personal info
        first_name: userData.first_name,
        last_name: userData.last_name,
        name: userData.name || `${userData.first_name || ''} ${userData.last_name || ''}`,
        email: userData.email,
        secondary_email: userData.secondary_email,
        phone: userData.phone,
        // Keep the status value (required by backend)
        status: userData.status || 1,
        
        // Company info
        company_name: userData.company_name || '',
        company_registration: userData.company_registration || '',
        
        // Address info
        apartment_number: userData.apartment_number || '',
        address: userData.address || '',
        city: userData.city || '',
        post_code: userData.post_code || '',
        country: userData.country || 'GB',
        
        // Service info (required by backend)
        service_type: userData.service_type || 'LinenHire',
        property_range_id: userData.property_range_id || 3,
        user_type: userData.user_type || 0
      });
      
      // Fetch user stats
    /*   try {
        const statsResponse = await userService.getUserStats(currentUser.id);
        if (statsResponse && statsResponse.data) {
          setUserStats(statsResponse.data);
        }
      } catch (statsError) {
        console.error('Error fetching user stats:', statsError);
      } */
    } catch (error) {
      notification.error({
        message: 'Error',
        description: 'Failed to load user profile information.'
      });
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle profile update
  const handleProfileUpdate = async (values) => {
    try {
      setSaveLoading(true);
      
      // Merge with existing user data
      // Making sure we're sending all the fields required by the Laravel controller
      const updatedUserData = {
        ...values,
        name: `${values.first_name} ${values.last_name}`,
        // Set default status to active if not explicitly provided
        status: user.status || 1,
        // Keep existing user_type if available
        user_type: user.user_type || 0,
        // Maintain any roles the user currently has
        roles: user.roles && user.roles.length > 0 ? user.roles[0].name : 'customer'
      };
      
      // Call API to update user
      const response = await userService.updateUser(user.id, updatedUserData);
      
      if (response) {
        notification.success({
          message: 'Profile Updated',
          description: 'Your profile information has been updated successfully.'
        });
        
        // Update local storage with new user info
        const currentUser = await authService.getCurrentUser();
        const updatedUser = {
          ...currentUser,
          name: `${values.first_name} ${values.last_name}`,
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          secondary_email: values.secondary_email,
          phone: values.phone,
          company_name: values.company_name,
          company_registration: values.company_registration
        };
        localStorage.setItem('user', JSON.stringify(updatedUser));
        
        // Refresh user data
        fetchUserData();
        
        // Exit edit mode
        setEditMode(false);
      }
    } catch (error) {
      notification.error({
        message: 'Update Failed',
        description: error.message || 'Failed to update profile information.'
      });
    } finally {
      setSaveLoading(false);
    }
  };

  // Handle password update
  const handlePasswordUpdate = async (values) => {
    if (!user || !user.id) {
      notification.error({
        message: 'Error',
        description: 'User information not available. Please reload the page.'
      });
      return;
    }
    
    // Format password data for backend
    const passwordData = {
      current_password: values.current_password,
      password: values.password,
      password_confirmation: values.confirm_password
    };
    
    // Use the password update hook
    const success = await updatePassword(user.id, passwordData);
    
    if (success) {
      // Reset the form on success
      passwordForm.resetFields();
    }
  };

  const handleAvatarUpload = async (file) => {
    if (!file.type?.startsWith('image/')) {
      notification.error({
        message: 'Invalid file',
        description: 'Please select an image file.'
      });
      return Upload.LIST_IGNORE;
    }

    if (file.size > 4 * 1024 * 1024) {
      notification.error({
        message: 'File too large',
        description: 'User photo must be 4MB or smaller.'
      });
      return Upload.LIST_IGNORE;
    }

    try {
      setAvatarUploading(true);
      const response = await userService.uploadAvatar(user.id, file);
      const updatedUser = response.user || response.data || { ...user, avatar: response.avatar };
      setUser(updatedUser);

      const currentUser = await authService.getCurrentUser();
      localStorage.setItem('user', JSON.stringify({
        ...currentUser,
        avatar: updatedUser.avatar
      }));

      notification.success({
        message: 'Photo Updated',
        description: 'Your profile photo has been updated.'
      });
    } catch (error) {
      notification.error({
        message: 'Upload Failed',
        description: error.message || 'Failed to upload profile photo.'
      });
    } finally {
      setAvatarUploading(false);
    }

    return false;
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Render profile content
  const renderProfileContent = () => {
    if (!user) return null;
    
    if (editMode) {
      return (
        <Form
          form={profileForm}
          layout="vertical"
          onFinish={handleProfileUpdate}
        >
          {/* Hidden fields required by the Laravel backend */}
          <Form.Item name="name" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="status" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="service_type" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="property_range_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="user_type" hidden>
            <Input />
          </Form.Item>
          
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="first_name"
                label="First Name"
                rules={[
                  { required: true, message: 'Please enter your first name' },
                  { pattern: /^[a-zA-Z ]*$/, message: 'Name must only contain letters' }
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="First Name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="last_name"
                label="Last Name"
                rules={[
                  { required: true, message: 'Please enter your last name' },
                  { pattern: /^[a-zA-Z ]*$/, message: 'Name must only contain letters' }
                ]}
              >
                <Input prefix={<UserOutlined />} placeholder="Last Name" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[
                  { required: true, message: 'Please enter your email' },
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Email" disabled />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="secondary_email"
                label="Secondary Email"
                rules={[
                  { type: 'email', message: 'Please enter a valid email' }
                ]}
              >
                <Input prefix={<MailOutlined />} placeholder="Secondary Email (Optional)" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="phone"
                label="Phone Number"
                rules={[
                  { required: true, message: 'Please enter your phone number' },
                  { max: 20, message: 'Phone number must be less than 20 characters' }
                ]}
              >
                <Input prefix={<PhoneOutlined />} placeholder="Phone Number" />
              </Form.Item>
            </Col>
          </Row>
          
          <Divider orientation="left">Company Information</Divider>
          
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="company_name"
                label="Company Name"
              >
                <Input prefix={<BuildOutlined />} placeholder="Company Name" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="company_registration"
                label="Company Registration Number"
              >
                <Input placeholder="Company Registration" />
              </Form.Item>
            </Col>
          </Row>
          
          <Divider orientation="left">Address Information</Divider>
          
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="apartment_number"
                label="Apartment/Building Number"
                rules={[{ required: true, message: 'Please enter your apartment/building number' }]}
              >
                <Input prefix={<HomeOutlined />} placeholder="Apartment/Building Number" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="address"
                label="Address"
                rules={[{ required: true, message: 'Please enter your address' }]}
              >
                <Input prefix={<HomeOutlined />} placeholder="Address" />
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                name="city"
                label="City"
                rules={[{ required: true, message: 'Please enter your city' }]}
              >
                <Input placeholder="City" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="post_code"
                label="Post Code"
                rules={[{ required: true, message: 'Please enter your post code' }]}
              >
                <Input placeholder="Post Code" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                name="country"
                label="Country"
                rules={[{ required: true, message: 'Please select your country' }]}
              >
                <Select placeholder="Select Country">
                  {countries.map(country => (
                    <Option key={country.code} value={country.code}>{country.name}</Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={saveLoading}
                icon={<SaveOutlined />}
                className="bg-golden-navy"
              >
                Save Changes
              </Button>
              <Button onClick={() => setEditMode(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      );
    }
    
    return (
      <div>
        <div className="flex justify-between mb-4">
          <h2 className="text-xl font-semibold">Profile Information</h2>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => setEditMode(true)}
            className="bg-golden-navy"
          >
            Edit Profile
          </Button>
        </div>
        
        <Row gutter={24}>
          <Col xs={24} md={8}>
            <Card className="mb-4">
              <div className="text-center">
                <Avatar
                  size={88}
                  src={resolveAssetUrl(user.avatar)}
                  icon={<UserOutlined />}
                  className="mb-4"
                />
                <h3 className="text-lg font-medium">{user.name || `${user.first_name} ${user.last_name}`}</h3>
                <p className="text-gray-500">{user.email}</p>
                <Upload
                  accept="image/*"
                  showUploadList={false}
                  beforeUpload={handleAvatarUpload}
                >
                  <Button
                    size="small"
                    icon={<UploadOutlined />}
                    loading={avatarUploading}
                    className="mt-3"
                  >
                    Upload Photo
                  </Button>
                </Upload>
                <Divider />
                <div className="text-left">
                  <p className="flex items-center">
                    <MailOutlined className="mr-2" /> {user.email}
                  </p>
                  {user.secondary_email && (
                    <p className="flex items-center">
                      <MailOutlined className="mr-2" /> {user.secondary_email} <span className="ml-1 text-xs text-gray-500">(Secondary)</span>
                    </p>
                  )}
                  {user.phone && (
                    <p className="flex items-center">
                      <PhoneOutlined className="mr-2" /> {user.phone}
                    </p>
                  )}
                </div>
              </div>
            </Card>

            <Card title="Account Information" className="mb-4">
              <Statistic
                title="Account Created"
                value={formatDate(user.created_at)}
                prefix={<CalendarOutlined />}
              />
              <Divider />
              <Statistic
                title="Last Login"
                value={formatDate(user.last_login) || 'Never'}
                prefix={<CalendarOutlined />}
              />
            </Card>
          </Col>
          
          <Col xs={24} md={16}>
            <Card className="mb-4">
              <Descriptions title="Personal Information" bordered column={1}>
                <Descriptions.Item label="Name">{user.name || `${user.first_name} ${user.last_name}`}</Descriptions.Item>
                <Descriptions.Item label="Email">{user.email}</Descriptions.Item>
                {user.secondary_email && (
                  <Descriptions.Item label="Secondary Email">{user.secondary_email}</Descriptions.Item>
                )}
                <Descriptions.Item label="Phone">{user.phone}</Descriptions.Item>
              </Descriptions>
            </Card>
            
            {(user.company_name || user.company_registration) && (
              <Card className="mb-4">
                <Descriptions title="Company Information" bordered column={1}>
                  {user.company_name && (
                    <Descriptions.Item label="Company Name">{user.company_name}</Descriptions.Item>
                  )}
                  {user.company_registration && (
                    <Descriptions.Item label="Registration Number">{user.company_registration}</Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
            )}
            
            <Card>
              <Descriptions title="Address Information" bordered column={1}>
                <Descriptions.Item label="Apartment/Building Number">{user.apartment_number || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Address">{user.address || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="City">{user.city || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Post Code">{user.post_code || 'N/A'}</Descriptions.Item>
                <Descriptions.Item label="Country">
                  {countries.find(c => c.code === user.country)?.name || user.country || 'N/A'}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  // Render password tab content
  const renderPasswordContent = () => {
    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">Change Password</h2>
        
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handlePasswordUpdate}
        >
          <Form.Item
            name="current_password"
            label="Current Password"
            rules={[
              { required: true, message: 'Please enter your current password' }
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Current Password" 
            />
          </Form.Item>
          
          <Form.Item
            name="password"
            label="New Password"
            rules={[
              { required: true, message: 'Please enter your new password' },
              { min: 8, message: 'Password must be at least 8 characters' }
            ]}
            hasFeedback
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="New Password" 
            />
          </Form.Item>
          
          <Form.Item
            name="confirm_password"
            label="Confirm New Password"
            dependencies={['password']}
            hasFeedback
            rules={[
              { required: true, message: 'Please confirm your new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('The two passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password 
              prefix={<LockOutlined />} 
              placeholder="Confirm Password" 
            />
          </Form.Item>
          
          <Form.Item>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={passwordLoading}
              className="bg-golden-navy"
            >
              Update Password
            </Button>
          </Form.Item>
        </Form>
      </div>
    );
  };

  // Render account stats tab content
  const renderAccountStatsContent = () => {
    return (
      <div>
        <h2 className="text-xl font-semibold mb-4">Account Statistics</h2>
        
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Properties"
                value={userStats.properties || 0}
                prefix={<HomeOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Active Orders"
                value={userStats.activeOrders || 0}
                prefix={<ShoppingCartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Completed Orders"
                value={userStats.completedOrders || 0}
                prefix={<ShoppingCartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Spent"
                value={userStats.totalSpent ? `£${parseFloat(userStats.totalSpent).toFixed(2)}` : '£0.00'}
                prefix="£"
              />
            </Card>
          </Col>
        </Row>
        
        <div className="mt-4">
          <Card>
            <h3 className="font-medium mb-2">Last Order</h3>
            {userStats.lastOrder ? (
              <div>
                <p>Date: {formatDate(userStats.lastOrder)}</p>
                {/* Additional order details could be displayed here */}
              </div>
            ) : (
              <p>No orders yet</p>
            )}
          </Card>
        </div>
      </div>
    );
  };

  // Main render method
  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-golden-navy flex items-center">
          <UserOutlined className="mr-2" />
          My Profile
        </h1>
      </div>
      
      <Spin spinning={loading}>
        <Card className="shadow-md">
          <Tabs defaultActiveKey="profile">
            <TabPane 
              tab={<span><UserOutlined /> Profile</span>}
              key="profile"
            >
              {renderProfileContent()}
            </TabPane>
            
            <TabPane 
              tab={<span><LockOutlined /> Password</span>}
              key="password"
            >
              {renderPasswordContent()}
            </TabPane>
            
          {/*   <TabPane 
              tab={<span><ShoppingCartOutlined /> Account Stats</span>}
              key="stats"
            >
              {renderAccountStatsContent()}
            </TabPane> */}
          </Tabs>
        </Card>
      </Spin>
    </div>
  );
};

export default UserProfile;
